import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Card, Spinner, Row, Col, Tab, Tabs, Badge, Accordion, Modal } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaVideo, FaClipboardCheck, FaPlus, FaCog, FaList, FaFilePdf, FaToggleOn, FaToggleOff, FaChevronDown, FaChevronRight, FaEye, FaCheckCircle, FaTimesCircle, FaPlayCircle } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/Capacitaciones.css';
import CapacitacionCard from '../components/CapacitacionCard';

const API_URL = (process.env.REACT_APP_API_URL || "/").replace(/\/$/, '');

// Mapeo de roles
const ROLES_MAP = {
   0: 'Todos',
   1: 'Administración',
   2: 'Inspección',
   3: 'Taller',
   4: 'Conducción'
};

const CargarCapacitaciones = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('modulo');
  const [categorias, setCategorias] = useState([]);
  const [modulosCount, setModulosCount] = useState(0);
  const [loadingModulos, setLoadingModulos] = useState(false);
  const [loadingCategoria, setLoadingCategoria] = useState(false);
  
  // Estados para vista expandida
  const [expandedCapacitacion, setExpandedCapacitacion] = useState(null);
  const [modulosPorCategoria, setModulosPorCategoria] = useState({});
  const [evaluacionesPorModulo, setEvaluacionesPorModulo] = useState({});
  const [loadingModulosView, setLoadingModulosView] = useState({});
  const [loadingEvaluaciones, setLoadingEvaluaciones] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  const [nuevaCategoria, setNuevaCategoria] = useState({
    nombre: '',
    descripcion: '',
    activo: true,
    rol_destinado: '0'
  });

  const usuario = JSON.parse(localStorage.getItem('user'));

  const [form, setForm] = useState({
    categoria_id: '',
    titulo: '',
    orden: '',
    video_url: '',
    tipo_contenido: 'youtube',
    requiere_aprobacion: true
  });

  const [archivoFile, setArchivoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargarCategorias = () => {
    const token = localStorage.getItem('token');
    
    fetch(`${API_URL}/api/capacitaciones/categorias`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setCategorias(data.data);
      })
      .catch(() => toast.error('Error al cargar categorías'));
  };

  const toggleActivoCategoria = async (id, estadoActual) => {
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/capacitaciones/categorias/${id}/toggle-activo`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.mensaje);
        cargarCategorias(); // Recargar lista
      } else {
        toast.error(data.error || 'Error al cambiar estado');
      }
    } catch (error) {
      toast.error('Error al cambiar estado de la capacitación');
    }
  };

  // Cargar módulos de una capacitación
  const cargarModulosDeCapacitacion = async (categoriaId) => {
    if (modulosPorCategoria[categoriaId]) {
      // Ya están cargados
      return;
    }

    setLoadingModulosView(prev => ({ ...prev, [categoriaId]: true }));
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/capacitaciones/modulos/${categoriaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (data.success) {
        setModulosPorCategoria(prev => ({
          ...prev,
          [categoriaId]: data.data
        }));
      }
    } catch (error) {
      toast.error('Error al cargar módulos');
    } finally {
      setLoadingModulosView(prev => ({ ...prev, [categoriaId]: false }));
    }
  };

  // Cargar evaluaciones de un módulo
  const cargarEvaluacionesDeModulo = async (moduloId) => {
    if (evaluacionesPorModulo[moduloId]) {
      // Ya están cargadas
      return;
    }

    setLoadingEvaluaciones(prev => ({ ...prev, [moduloId]: true }));
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/capacitaciones/evaluacion/${moduloId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();

      if (data.success) {
        setEvaluacionesPorModulo(prev => ({
          ...prev,
          [moduloId]: data.data
        }));
      }
    } catch (error) {
      toast.error('Error al cargar evaluaciones');
    } finally {
      setLoadingEvaluaciones(prev => ({ ...prev, [moduloId]: false }));
    }
  };

  // Toggle expansión de capacitación
  const toggleCapacitacion = (categoriaId) => {
    if (expandedCapacitacion === categoriaId) {
      setExpandedCapacitacion(null);
    } else {
      setExpandedCapacitacion(categoriaId);
      cargarModulosDeCapacitacion(categoriaId);
    }
  };

  // Extraer ID de YouTube
  const extractYouTubeID = (url) => {
    if (!url) return null;
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  // Abrir modal de video
  const abrirVideo = (modulo) => {
    setSelectedVideo(modulo);
    setShowVideoModal(true);
  };

  useEffect(() => {
    cargarCategorias();
  }, []);

  const obtenerSiguienteModulo = async (categoriaId) => {
    if (!categoriaId) {
      setModulosCount(0);
      setForm(prev => ({ ...prev, orden: '' }));
      return;
    }

    setLoadingModulos(true);
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/capacitaciones/modulos/${categoriaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      
      if (data.success && Array.isArray(data.data)) {
        const siguienteNumero = data.data.length + 1;
        setModulosCount(data.data.length);
        setForm(prev => ({ ...prev, orden: siguienteNumero.toString() }));
      } else {
        setModulosCount(0);
        setForm(prev => ({ ...prev, orden: '1' }));
      }
    } catch {
      setForm(prev => ({ ...prev, orden: '1' }));
    } finally {
      setLoadingModulos(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'categoria_id') {
      setForm(prev => ({ ...prev, [name]: value }));
      obtenerSiguienteModulo(value);
    } else {
      setForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleNuevaCategoriaChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNuevaCategoria(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const crearCategoria = async () => {
    if (!nuevaCategoria.nombre.trim()) {
      toast.error('Ingresá un nombre para la capacitación');
      return;
    }

    setLoadingCategoria(true);
    try {
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/capacitaciones/categorias`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(nuevaCategoria)
      });

      const data = await res.json();
      if (!data.success) throw new Error('Error al crear capacitación');

      toast.success('✅ Capacitación creada correctamente');
      setNuevaCategoria({ nombre: '', descripcion: '', activo: true, rol_destinado: '0' });
      cargarCategorias();
      setActiveTab('gestion'); // Ir al listado después de crear
    } catch {
      toast.error('No se pudo crear la capacitación');
    } finally {
      setLoadingCategoria(false);
    }
  };

  const handleArchivoUpload = async (moduloId) => {
    const formData = new FormData();
    formData.append('archivo', archivoFile);

    const usuario = JSON.parse(localStorage.getItem('user'));
    const token = localStorage.getItem('token');

    const headers = {
      'Authorization': `Bearer ${token}`,
      'x-user-id': usuario?.CdEmpleado || usuario?.Legajo || '',
      'x-user-name': usuario?.Nombre || '',
      'x-user-role': String(usuario?.IdRol || ''),
      'x-base-id': usuario?.base_id || ''
    };

    const endpoint = `${API_URL}/api/capacitaciones/modulos/${moduloId}/archivo`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: formData
    });

    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Error al subir archivo');

    return { url: data.video_url, tipo: data.tipo_contenido };
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.categoria_id || !form.titulo || !form.orden) {
      toast.error('Completá todos los campos obligatorios');
      return;
    }

    // Validar que tenga al menos URL o archivo
    if (!form.video_url && !archivoFile) {
      toast.error('Debés ingresar una URL de YouTube o subir un archivo');
      return;
    }

    setLoading(true);

    try {
      let finalVideoUrl = form.video_url;
      let finalTipoContenido = form.tipo_contenido;

      // Primero crear el módulo
      const payload = {
        ...form,
        video_url: form.video_url || '', // Enviar la URL si existe
        tipo_contenido: form.tipo_contenido,
        subido_por: usuario?.Legajo
      };

      // Construir headers de usuario
      const token = localStorage.getItem('token');
      
      const usuarioHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-user-id': usuario?.CdEmpleado || usuario?.Legajo || '',
        'x-user-name': usuario?.Nombre || '',
        'x-user-role': String(usuario?.IdRol || ''),
        'x-base-id': usuario?.base_id || ''
      };

      const resModulo = await fetch(`${API_URL}/api/capacitaciones/modulos`, {
        method: 'POST',
        headers: usuarioHeaders,
        body: JSON.stringify(payload)
      });
      const dataModulo = await resModulo.json();
      if (!dataModulo.success || !dataModulo.id) throw new Error('Error al registrar módulo');
      const moduloId = dataModulo.id;

      // Si hay archivo, subirlo y actualizar el módulo
      if (archivoFile) {
        const resultado = await handleArchivoUpload(moduloId);
        finalVideoUrl = resultado.url;
        finalTipoContenido = resultado.tipo;

        // Actualizar el módulo con la URL y tipo
        await fetch(`${API_URL}/api/capacitaciones/modulos/${moduloId}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            video_url: finalVideoUrl,
            tipo_contenido: finalTipoContenido
          })
        });
      }

      toast.success('✅ Módulo creado correctamente');
      setForm({ categoria_id: '', titulo: '', orden: '', video_url: '', tipo_contenido: 'youtube', requiere_aprobacion: true });
      setArchivoFile(null);
      setModulosCount(0);
    } catch {
      toast.error('No se pudo crear el módulo');
    } finally {
      setLoading(false);
    }
  };

  const categoriaSeleccionada = categorias.find(c => c.id.toString() === form.categoria_id);

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="capacitaciones-header mb-4">
        <h2 className="mb-1">
          <FaBook className="me-2" />
          Gestión de Capacitaciones
        </h2>
        <p className="text-muted mb-0">Administrá módulos, capacitaciones y evaluaciones</p>
      </div>

      {/* Tabs principales */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="capacitaciones-tabs mb-4"
      >
        {/* TAB 1: Cargar Módulo */}
        <Tab 
          eventKey="modulo" 
          title={<><FaVideo className="me-2" />Cargar Módulo</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-primary">
                  <FaVideo />
                </span>
                Agregar nuevo módulo
              </h5>

              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">Capacitación</Form.Label>
                      <Form.Select 
                        name="categoria_id" 
                        value={form.categoria_id} 
                        onChange={handleChange} 
                        required
                        className="form-control-lg"
                      >
                        <option value="">Seleccionar capacitación...</option>
                        {categorias.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        N° de Módulo
                        {loadingModulos && <Spinner animation="border" size="sm" className="ms-2" />}
                      </Form.Label>
                      <Form.Control 
                        type="number" 
                        name="orden" 
                        value={form.orden} 
                        onChange={handleChange} 
                        required
                        className="form-control-lg"
                        min="1"
                        disabled={loadingModulos}
                      />
                      {modulosCount > 0 && (
                        <Form.Text className="text-muted">
                          {modulosCount} módulo(s) existente(s)
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {categoriaSeleccionada && (
                  <div className="alert alert-light border mt-3 mb-3">
                    <strong>{categoriaSeleccionada.nombre}</strong>
                    {categoriaSeleccionada.descripcion && (
                      <p className="mb-0 mt-1 small text-muted">{categoriaSeleccionada.descripcion}</p>
                    )}
                  </div>
                )}

                <Form.Group className="mt-3">
                  <Form.Label className="form-label-custom">Título del módulo</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="titulo" 
                    value={form.titulo} 
                    onChange={handleChange} 
                    required
                    placeholder="Ej: Introducción a la seguridad vial"
                  />
                </Form.Group>

                {/* Selector de tipo de contenido */}
                <Form.Group className="mt-3">
                  <Form.Label className="form-label-custom">Tipo de contenido</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="tipo-youtube"
                      name="tipo_contenido"
                      label={<><FaVideo className="me-1" /> Video de YouTube</>}
                      value="youtube"
                      checked={form.tipo_contenido === 'youtube'}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="radio"
                      id="tipo-video"
                      name="tipo_contenido"
                      label={<><FaVideo className="me-1" /> Video (subir archivo)</>}
                      value="video"
                      checked={form.tipo_contenido === 'video'}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="radio"
                      id="tipo-pdf"
                      name="tipo_contenido"
                      label={<><FaFilePdf className="me-1" /> PDF</>}
                      value="pdf"
                      checked={form.tipo_contenido === 'pdf'}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>

                <Row className="mt-3 g-3">
                  {form.tipo_contenido === 'youtube' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">URL del video de YouTube</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="video_url" 
                          value={form.video_url} 
                          onChange={handleChange} 
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </Form.Group>
                    </Col>
                  )}
                  {form.tipo_contenido === 'video' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">Subir video</Form.Label>
                        <Form.Control 
                          type="file" 
                          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                          onChange={(e) => setArchivoFile(e.target.files[0])}
                        />
                        <Form.Text className="text-muted">
                          Formatos aceptados: MP4, WebM, MOV, AVI (máx. 100MB)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                  {form.tipo_contenido === 'pdf' && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="form-label-custom">Subir PDF</Form.Label>
                        <Form.Control 
                          type="file" 
                          accept="application/pdf"
                          onChange={(e) => setArchivoFile(e.target.files[0])}
                        />
                        <Form.Text className="text-muted">
                          Formato PDF (máx. 100MB)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <Form.Group className="mt-4">
                  <Form.Check
                    type="switch"
                    id="requiere_aprobacion"
                    label="Requiere evaluación para aprobar"
                    name="requiere_aprobacion"
                    checked={form.requiere_aprobacion}
                    onChange={handleChange}
                  />
                </Form.Group>

                <div className="d-flex gap-2 mt-4 pt-2">
                  <Button 
                    type="submit" 
                    className="btn-custom-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <><Spinner animation="border" size="sm" className="me-2" />Guardando...</>
                    ) : (
                      <><FaPlus className="me-2" />Crear Módulo</>
                    )}
                  </Button>
                  
                  {form.requiere_aprobacion && form.categoria_id && (
                    <Button 
                      className="btn-custom-outline"
                      onClick={() => navigate('/admin/capacitaciones/evaluacion')}
                    >
                      <FaClipboardCheck className="me-2" />
                      Cargar Evaluación
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 2: Nueva Capacitación */}
        <Tab 
          eventKey="capacitacion" 
          title={<><FaPlus className="me-2" />Nueva Capacitación</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-success">
                  <FaBook />
                </span>
                Crear nueva capacitación
              </h5>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="form-label-custom">Nombre</Form.Label>
                    <Form.Control
                      type="text"
                      name="nombre"
                      value={nuevaCategoria.nombre}
                      onChange={handleNuevaCategoriaChange}
                      placeholder="Ej: Seguridad e Higiene"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="form-label-custom">Destinado a</Form.Label>
                    <Form.Select
                      name="rol_destinado"
                      value={nuevaCategoria.rol_destinado}
                      onChange={handleNuevaCategoriaChange}
                    >
                      <option value="0">Todos los roles</option>
                      <option value="1">Administración</option>
                      <option value="2">Inspección</option>
                      <option value="3">Taller</option>
                      <option value="4">Conducción</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-3">
                <Form.Label className="form-label-custom">Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="descripcion"
                  value={nuevaCategoria.descripcion}
                  onChange={handleNuevaCategoriaChange}
                  placeholder="Descripción breve de la capacitación..."
                />
              </Form.Group>

              <Form.Group className="mt-4">
                <Form.Check
                  type="switch"
                  id="activo"
                  label="Capacitación activa (visible para usuarios)"
                  name="activo"
                  checked={nuevaCategoria.activo}
                  onChange={handleNuevaCategoriaChange}
                />
              </Form.Group>

              <Button 
                className="btn-custom-success mt-4"
                onClick={crearCategoria}
                disabled={loadingCategoria}
              >
                {loadingCategoria ? (
                  <><Spinner animation="border" size="sm" className="me-2" />Creando...</>
                ) : (
                  <><FaPlus className="me-2" />Crear Capacitación</>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 3: Gestionar Capacitaciones - VISTA MEJORADA */}
        <Tab 
          eventKey="gestion" 
          title={<><FaList className="me-2" />Ver Capacitaciones</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="card-title-custom mb-4">
                <span className="title-icon bg-info">
                  <FaCog />
                </span>
                Capacitaciones existentes
              </h5>
              {categorias.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaBook size={48} className="mb-3 opacity-50" />
                  <p>No hay capacitaciones creadas aún</p>
                  <Button 
                    className="btn-custom-primary"
                    onClick={() => setActiveTab('capacitacion')}
                  >
                    <FaPlus className="me-2" />
                    Crear primera capacitación
                  </Button>
                </div>
              ) : (
                <div className="capacitaciones-expandible">
                  {categorias.map(cat => {
                    const isExpanded = expandedCapacitacion === cat.id;
                    const modulos = modulosPorCategoria[cat.id] || [];
                    const isLoadingModulos = loadingModulosView[cat.id];

                    return (
                      <div key={cat.id} className="capacitacion-item mb-3">
                        {/* Header de Capacitación */}
                        <div 
                          className={`capacitacion-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleCapacitacion(cat.id)}
                          style={{ 
                            cursor: 'pointer',
                            opacity: cat.activo ? 1 : 0.6
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="expand-icon me-3">
                              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold">{cat.nombre}</h6>
                              {cat.descripcion && (
                                <p className="mb-0 small text-muted">{cat.descripcion}</p>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Badge bg="secondary" className="badge-sector">
                                {ROLES_MAP[cat.rol_destinado] || 'Todos'}
                              </Badge>
                              <Badge bg={cat.activo ? 'success' : 'secondary'}>
                                {cat.activo ? 'Activa' : 'Inactiva'}
                              </Badge>
                              <Button
                                size="sm"
                                variant={cat.activo ? "outline-danger" : "outline-success"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleActivoCategoria(cat.id, cat.activo);
                                }}
                                title={cat.activo ? "Desactivar" : "Activar"}
                              >
                                {cat.activo ? <FaToggleOff /> : <FaToggleOn />}
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Contenido Expandido: Módulos */}
                        {isExpanded && (
                          <div className="capacitacion-content">
                            {isLoadingModulos ? (
                              <div className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                                <p className="text-muted mt-2">Cargando módulos...</p>
                              </div>
                            ) : modulos.length === 0 ? (
                              <div className="text-center py-4 text-muted">
                                <FaVideo size={32} className="mb-2 opacity-50" />
                                <p>No hay módulos creados para esta capacitación</p>
                              </div>
                            ) : (
                              <Accordion flush>
                                {modulos.map((modulo, idx) => {
                                  const evaluaciones = evaluacionesPorModulo[modulo.id] || [];
                                  const isLoadingEval = loadingEvaluaciones[modulo.id];

                                  return (
                                    <Accordion.Item eventKey={idx.toString()} key={modulo.id}>
                                      <Accordion.Header>
                                        <div className="d-flex align-items-center w-100">
                                          <Badge bg="primary" className="me-3">
                                            Módulo {modulo.orden}
                                          </Badge>
                                          <span className="fw-medium">{modulo.titulo}</span>
                                          <div className="ms-auto me-3 d-flex gap-2">
                                            {modulo.tipo_contenido === 'youtube' && (
                                              <Badge bg="danger" pill>
                                                <FaVideo className="me-1" /> YouTube
                                              </Badge>
                                            )}
                                            {modulo.tipo_contenido === 'video' && (
                                              <Badge bg="info" pill>
                                                <FaPlayCircle className="me-1" /> Video
                                              </Badge>
                                            )}
                                            {modulo.tipo_contenido === 'pdf' && (
                                              <Badge bg="warning" pill>
                                                <FaFilePdf className="me-1" /> PDF
                                              </Badge>
                                            )}
                                            {modulo.requiere_aprobacion && (
                                              <Badge bg="success" pill>
                                                <FaClipboardCheck className="me-1" /> Evaluación
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </Accordion.Header>
                                      <Accordion.Body>
                                        <Row>
                                          {/* Columna del Video/PDF */}
                                          <Col md={7}>
                                            <div className="video-container mb-3">
                                              {modulo.tipo_contenido === 'youtube' && modulo.video_url && (
                                                <div className="ratio ratio-16x9">
                                                  <iframe
                                                    src={`https://www.youtube.com/embed/${extractYouTubeID(modulo.video_url)}`}
                                                    title={modulo.titulo}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                  ></iframe>
                                                </div>
                                              )}
                                              {modulo.tipo_contenido === 'video' && modulo.video_url && (
                                                <div className="ratio ratio-16x9">
                                                  <video controls>
                                                    <source src={modulo.video_url} type="video/mp4" />
                                                    Tu navegador no soporta el elemento de video.
                                                  </video>
                                                </div>
                                              )}
                                              {modulo.tipo_contenido === 'pdf' && modulo.video_url && (
                                                <div className="pdf-preview text-center p-4 bg-light border rounded">
                                                  <FaFilePdf size={48} className="text-danger mb-3" />
                                                  <p className="mb-3">Documento PDF disponible</p>
                                                  <Button
                                                    variant="outline-danger"
                                                    href={modulo.video_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    <FaEye className="me-2" />
                                                    Ver PDF
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          </Col>

                                          {/* Columna de Evaluaciones */}
                                          <Col md={5}>
                                            {modulo.requiere_aprobacion ? (
                                              <div className="evaluaciones-section">
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                  <h6 className="mb-0">
                                                    <FaClipboardCheck className="me-2 text-success" />
                                                    Evaluación
                                                  </h6>
                                                  <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => cargarEvaluacionesDeModulo(modulo.id)}
                                                  >
                                                    {evaluaciones.length > 0 ? 'Recargar' : 'Ver preguntas'}
                                                  </Button>
                                                </div>

                                                {isLoadingEval ? (
                                                  <div className="text-center py-3">
                                                    <Spinner animation="border" size="sm" />
                                                  </div>
                                                ) : evaluaciones.length === 0 ? (
                                                  <div className="alert alert-warning small">
                                                    No hay preguntas cargadas aún
                                                  </div>
                                                ) : (
                                                  <div className="preguntas-list">
                                                    <p className="small text-muted mb-2">
                                                      {evaluaciones.length} pregunta(s) cargada(s)
                                                    </p>
                                                    {evaluaciones.map((pregunta, pIdx) => {
                                                      // Determinar el tipo de pregunta
                                                      const esTrueFalse = pregunta.respuesta_correcta === 'true' || pregunta.respuesta_correcta === 'false';
                                                      
                                                      return (
                                                        <Card key={pregunta.id} className="mb-2 border-0 shadow-sm">
                                                          <Card.Body className="p-3">
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                              <p className="small mb-0 fw-medium flex-grow-1">
                                                                {pIdx + 1}. {pregunta.pregunta}
                                                              </p>
                                                              {esTrueFalse && (
                                                                <Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                                                  V/F
                                                                </Badge>
                                                              )}
                                                            </div>
                                                            
                                                            {/* Respuesta correcta destacada */}
                                                            <div className="respuesta-correcta-badge mb-2">
                                                              <Badge bg="success" className="d-inline-flex align-items-center">
                                                                <FaCheckCircle className="me-1" />
                                                                Correcta: {pregunta.respuesta_correcta?.toUpperCase()}
                                                              </Badge>
                                                            </div>
                                                            
                                                            <div className="opciones-preguntas small">
                                                              {esTrueFalse ? (
                                                                /* Preguntas de Verdadero/Falso */
                                                                <>
                                                                  <div className={`opcion-item ${pregunta.respuesta_correcta === 'true' ? 'correcta' : ''}`}>
                                                                    <span className="opcion-letra">V</span>
                                                                    <span className="opcion-texto">Verdadero</span>
                                                                    {pregunta.respuesta_correcta === 'true' && (
                                                                      <FaCheckCircle className="ms-auto text-success" />
                                                                    )}
                                                                  </div>
                                                                  <div className={`opcion-item ${pregunta.respuesta_correcta === 'false' ? 'correcta' : ''}`}>
                                                                    <span className="opcion-letra">F</span>
                                                                    <span className="opcion-texto">Falso</span>
                                                                    {pregunta.respuesta_correcta === 'false' && (
                                                                      <FaCheckCircle className="ms-auto text-success" />
                                                                    )}
                                                                  </div>
                                                                </>
                                                              ) : (
                                                                /* Preguntas de opción múltiple */
                                                                ['a', 'b', 'c', 'd'].map(opcion => {
                                                                  if (!pregunta[`opcion_${opcion}`]) return null;
                                                                  const esCorrecta = pregunta.respuesta_correcta === opcion;
                                                                  return (
                                                                    <div 
                                                                      key={opcion} 
                                                                      className={`opcion-item ${esCorrecta ? 'correcta' : ''}`}
                                                                    >
                                                                      <span className="opcion-letra">{opcion.toUpperCase()}</span>
                                                                      <span className="opcion-texto">{pregunta[`opcion_${opcion}`]}</span>
                                                                      {esCorrecta && (
                                                                        <FaCheckCircle className="ms-auto text-success" />
                                                                      )}
                                                                    </div>
                                                                  );
                                                                })
                                                              )}
                                                            </div>
                                                          </Card.Body>
                                                        </Card>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <div className="alert alert-info">
                                                <small>Este módulo no requiere evaluación</small>
                                              </div>
                                            )}
                                          </Col>
                                        </Row>
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  );
                                })}
                              </Accordion>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 4: Evaluaciones */}
        <Tab 
          eventKey="evaluacion" 
          title={<><FaClipboardCheck className="me-2" />Evaluaciones</>}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-5 text-center">
              <div className="evaluaciones-empty">
                <FaClipboardCheck size={56} className="text-warning mb-3" />
                <h5>Gestión de Evaluaciones</h5>
                <p className="text-muted mb-4">
                  Creá y administrá las evaluaciones para cada módulo de capacitación
                </p>
                <Button 
                  className="btn-custom-warning"
                  onClick={() => navigate('/administrador/cargar-evaluacion')}
                >
                  <FaClipboardCheck className="me-2" />
                  Ir a Cargar Evaluaciones
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <ToastContainer position="bottom-right" />
    </Container>
  );
}

export default CargarCapacitaciones;